/**
 * CentralFluxosPage — Central de Fluxos VSM/Office
 *
 * Lista todos os mapas de fluxo de valor cadastrados na organização.
 * Fonte: tabela `projects` (organization_id) + contagem de ações vinculadas.
 *
 * Indicadores críticos derivados do JSONB data.estado_atual.dashboardMetrics:
 *   PLT  → dashboardMetrics.leadTime   (minutos)
 *   VAT  → PLT * efficiency / 100      (estimativa: valor agregado)
 *   Eficiência → dashboardMetrics.efficiency (%)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Search,
  Map as MapIcon,
  Factory,
  Layers,
  Clock,
  TrendingUp,
  CheckSquare,
  ExternalLink,
  Pencil,
  Copy,
  Trash2,
} from 'lucide-react';
import { FluxosListRibbonFilters } from '../components/fluxos/FluxosListRibbonFilters';
import { supabase } from '../lib/supabaseClient';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useProjectContext } from '../context/ProjectContext';
import { useProjectStore } from '../store/useProjectStore';
import { useAppToast } from '../components/AppToast';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { EnterpriseRibbon } from '../components/layout/EnterpriseRibbon';
import { RibbonDesktopButton, desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import type { ProjectStatus } from '../types/enterpriseProject';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
} from '../types/enterpriseProject';
import type { MapType } from '../types/common';
import type { CardData } from '../types';
import { calculateProjectMetrics } from '../utils/leanMetrics';
import { resolveProjectMapType } from '../utils/projectHydration';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Dados do JSONB data.estado_atual extraídos para listagem */
interface FluxoMetrics {
  plt: number | null;       // Process Lead Time em minutos
  vat: number | null;       // Value-Added Time em minutos (PLT × eficiência)
  efficiency: number | null; // Ciclo de Eficiência 0–100
}

interface FluxoRow {
  id: string;
  name: string;
  status: ProjectStatus;
  mapType: MapType | null;
  description: string | null;
  metrics: FluxoMetrics;
  actionsTotal: number;
  actionsDone: number;
  createdAt: string;
  /** true = salvo apenas no localStorage deste navegador (não sincronizado) */
  isLocalOnly: boolean;
}

type RowMenuState =
  | { mode: 'actions'; row: FluxoRow; x: number; y: number }
  | { mode: 'rename'; row: FluxoRow; x: number; y: number; draft: string }
  | { mode: 'confirm-delete'; row: FluxoRow; x: number; y: number };

/** Parâmetros e cards persistidos em estado_atual (usados para recálculo de métricas) */
interface EstadoAtualMetricsShape {
  mapType?: MapType;
  dashboardMetrics?: {
    leadTime?: number;
    efficiency?: number;
  };
  cards?: CardData[];
  globalDemand?: number;
  workTime?: number;
  taktTime?: number;
}

/** Shape mínimo do canvas Project armazenado em lean_workflow_projects */
interface CanvasProject {
  id: string;
  nome_projeto?: string | null;
  data_criacao?: string | null;
  status?: string | null;
  estado_atual?: EstadoAtualMetricsShape | null;
}

// Estrutura mínima do JSONB armazenado em projects.data
interface ProjectJsonData {
  estado_atual?: EstadoAtualMetricsShape;
}

// Linha bruta do Supabase — campos opcionais para tolerar variações de schema
interface RawProjectRow {
  id: string;
  name: string | null;
  nome_projeto: string | null;
  status: string | null;
  description: string | null;
  created_at: string;
  /** Colunas de métricas VSM (adicionadas por database_fluxos_metrics.sql) */
  flow_type?: string | null;    // 'Industrial' | 'Office'
  visibility?: string | null;
  plt_value?: number | null;    // PLT (Factory) ou TP (Office) em minutos
  vat_value?: number | null;    // VAT (Factory) ou TRA (Office) em minutos
  // JSONB presente apenas se o projeto foi criado pelo cloudProjectService
  data?: ProjectJsonData | null;
  [key: string]: unknown;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LOCAL_PROJECT_KEYS = ['lean_workflow_projects', 'lean_workflow_projects_org'] as const;

function readLocalProjects(key: string): Array<Record<string, unknown>> {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed as Array<Record<string, unknown>> : [];
  } catch {
    return [];
  }
}

function writeLocalProjects(key: string, projects: Array<Record<string, unknown>>) {
  localStorage.setItem(key, JSON.stringify(projects));
}

function updateLocalProject(id: string, nextName: string) {
  for (const key of LOCAL_PROJECT_KEYS) {
    const projects = readLocalProjects(key);
    if (!projects.some(project => project.id === id)) continue;
    writeLocalProjects(key, projects.map(project => (
      project.id === id
        ? { ...project, nome_projeto: nextName, name: nextName }
        : project
    )));
  }
}

function removeLocalProject(id: string) {
  for (const key of LOCAL_PROJECT_KEYS) {
    const projects = readLocalProjects(key);
    if (!projects.some(project => project.id === id)) continue;
    writeLocalProjects(key, projects.filter(project => project.id !== id));
  }
}

function duplicateLocalProject(id: string, nextId: string, nextName: string) {
  const projects = readLocalProjects('lean_workflow_projects');
  const source = projects.find(project => project.id === id);
  if (!source) return false;
  const clone = {
    ...source,
    id: nextId,
    nome_projeto: nextName,
    name: nextName,
    data_criacao: new Date().toISOString(),
  };
  writeLocalProjects('lean_workflow_projects', [clone, ...projects]);
  return true;
}

function isRetiredProject(project: { visibility?: string | null; status?: string | null }): boolean {
  return project.visibility === 'archived' || project.status === 'archived';
}

/** Apaga o mapa. Se a nuvem não tiver permissão de DELETE, arquiva para sumir da lista. */
async function retireCloudProject(id: string, orgId: string): Promise<void> {
  const { data: deleted, error: deleteError } = await supabase
    .from('projects')
    .delete()
    .eq('id', id)
    .eq('organization_id', orgId)
    .select('id');
  if (deleteError) throw new Error(deleteError.message);
  if ((deleted ?? []).length > 0) return;

  const archived = await supabase
    .from('projects')
    .update({ visibility: 'archived', status: 'archived' })
    .eq('id', id)
    .eq('organization_id', orgId);
  if (archived.error) {
    const visibilityOnly = await supabase
      .from('projects')
      .update({ visibility: 'archived' })
      .eq('id', id)
      .eq('organization_id', orgId);
    if (visibilityOnly.error) throw new Error(archived.error.message);
  }

  const { data: remaining, error: readError } = await supabase
    .from('projects')
    .select('id, visibility, status')
    .eq('id', id)
    .eq('organization_id', orgId)
    .maybeSingle();
  if (readError) throw new Error(readError.message);
  if (!remaining || isRetiredProject(remaining)) return;

  throw new Error('A nuvem recusou a exclusão deste mapa.');
}

function copyLabel(name: string): string {
  const base = name.trim() || 'Mapa';
  return base.endsWith('(cópia)') ? `${base} 2` : `${base} (cópia)`;
}

function placeMenu(x: number, y: number, height: number) {
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - 220)),
    y: Math.max(8, Math.min(y, window.innerHeight - height - 8)),
  };
}

const MAP_TYPE_LABELS: Record<MapType, string> = {
  FACTORY: 'Factory',
  OFFICE: 'Office',
};

const MAP_TYPE_COLORS: Record<MapType, string> = {
  FACTORY: 'bg-blue-500/10 text-blue-700 border border-blue-500/20',
  OFFICE:  'bg-violet-500/10 text-violet-700 border border-violet-500/20',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMinutes(min: number | null): string {
  if (min === null || min <= 0) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtPct(v: number | null): string {
  if (v === null || v < 0) return '—';
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function computeEfficiency(mapType: MapType | null, plt: number | null, vat: number | null): number | null {
  if (plt === null || vat === null || plt <= 0) return null;
  if (mapType === 'FACTORY') {
    // PCE = VAT / PLT × 100
    return Math.min(Math.round((vat / plt) * 1000) / 10, 100);
  } else if (mapType === 'OFFICE') {
    // Eficiência = TP / (TP + TRA) × 100
    const total = plt + vat;
    return total > 0 ? Math.min(Math.round((plt / total) * 1000) / 10, 100) : null;
  }
  return null;
}

/**
 * Recalcula PLT/VAT/Eficiência a partir dos cards persistidos em estado_atual.
 * As métricas do dashboard não são gravadas no banco; quando há cards salvos,
 * derivamos os indicadores em tempo de listagem usando a mesma engine Lean.
 */
function recomputeMetricsFromState(estado: EstadoAtualMetricsShape | undefined): FluxoMetrics | null {
  const cards = estado?.cards;
  if (!Array.isArray(cards) || cards.length === 0) return null;
  try {
    const workTime = typeof estado?.workTime === 'number' && estado.workTime > 0 ? estado.workTime : 480;
    const globalDemand = typeof estado?.globalDemand === 'number' && estado.globalDemand > 0 ? estado.globalDemand : 0;
    const taktTime = typeof estado?.taktTime === 'number' && estado.taktTime > 0
      ? estado.taktTime
      : (globalDemand > 0 ? workTime / globalDemand : 0);

    const dm = calculateProjectMetrics(cards, null, { globalDemand, workTime, taktTime });
    const plt = dm.leadTime > 0 ? dm.leadTime : null;
    const efficiency = dm.efficiency >= 0 ? dm.efficiency : null;
    if (plt === null && efficiency === null) return null;
    const vat = plt !== null && efficiency !== null ? Math.round((plt * efficiency / 100) * 100) / 100 : null;
    return { plt, vat, efficiency };
  } catch {
    return null;
  }
}

function deriveMetrics(raw: RawProjectRow): FluxoMetrics {
  // Prioridade 1: colunas DB (sincronizadas pelo canvas)
  const plt = typeof raw.plt_value === 'number' && raw.plt_value > 0 ? raw.plt_value : null;
  const vat = typeof raw.vat_value === 'number' && raw.vat_value >= 0 ? raw.vat_value : null;
  if (plt !== null && vat !== null) {
    const mapType = resolveProjectMapType({
      estado_atual: raw.data?.estado_atual,
      flow_type: raw.flow_type,
      enabledModules: raw.data?.enabledModules,
      nome_projeto: raw.name ?? raw.nome_projeto,
    });
    return { plt, vat, efficiency: computeEfficiency(mapType, plt, vat) };
  }

  // Prioridade 2: JSONB data.estado_atual.dashboardMetrics (projetos cloudProjectService legados)
  const m = raw.data?.estado_atual?.dashboardMetrics;
  const pltFb = typeof m?.leadTime === 'number' && m.leadTime > 0 ? m.leadTime : null;
  const effFb = typeof m?.efficiency === 'number' && m.efficiency >= 0 ? m.efficiency : null;
  if (pltFb !== null || effFb !== null) {
    const vatFb = pltFb !== null && effFb !== null ? Math.round(pltFb * effFb / 100 * 100) / 100 : null;
    return { plt: pltFb, vat: vatFb, efficiency: effFb };
  }

  // Prioridade 3: recálculo a partir dos cards salvos em estado_atual (engine Lean)
  const recomputed = recomputeMetricsFromState(raw.data?.estado_atual);
  if (recomputed) return recomputed;

  return { plt: null, vat: null, efficiency: null };
}

/** Convert a localStorage canvas Project to the RawProjectRow shape used by deriveMetrics */
function canvasProjectToRaw(p: CanvasProject): RawProjectRow {
  const m = p.estado_atual?.mapType;
  return {
    id: p.id,
    name: p.nome_projeto ?? null,
    nome_projeto: p.nome_projeto ?? null,
    // 'master' is the canvas equivalent of 'active'
    status: p.status === 'master' ? 'active' : (p.status ?? 'draft'),
    description: null,
    created_at: p.data_criacao ?? new Date(0).toISOString(),
    data: {
      estado_atual: {
        mapType: m,
        dashboardMetrics: p.estado_atual?.dashboardMetrics
          ? {
              leadTime: p.estado_atual.dashboardMetrics.leadTime ?? 0,
              efficiency: p.estado_atual.dashboardMetrics.efficiency ?? 0,
            }
          : undefined,
        // Repassa cards e parâmetros para permitir recálculo dos indicadores na listagem
        cards: p.estado_atual?.cards,
        globalDemand: p.estado_atual?.globalDemand,
        workTime: p.estado_atual?.workTime,
        taktTime: p.estado_atual?.taktTime,
      },
    },
  };
}

function toFluxoRow(
  raw: RawProjectRow,
  actionCounts: Map<string, { total: number; done: number }>,
  isLocalOnly = false,
): FluxoRow {
  const mapType = resolveProjectMapType({
    estado_atual: raw.data?.estado_atual,
    flow_type: raw.flow_type,
    enabledModules: raw.data?.enabledModules,
    nome_projeto: raw.name ?? raw.nome_projeto,
  });
  const counts = actionCounts.get(raw.id) ?? { total: 0, done: 0 };
  return {
    id: raw.id,
    name: raw.name ?? raw.nome_projeto ?? '(sem nome)',
    status: (raw.status as ProjectStatus) ?? 'draft',
    mapType,
    description: raw.description ?? null,
    metrics: deriveMetrics(raw),
    actionsTotal: counts.total,
    actionsDone: counts.done,
    createdAt: raw.created_at,
    isLocalOnly,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ProjectStatus }) {
  return (
    <EnterpriseBadge
      label={PROJECT_STATUS_LABELS[status] ?? status}
      color={PROJECT_STATUS_COLORS[status] ?? ''}
    />
  );
}

function MapTypeBadge({ type }: { type: MapType | null }) {
  if (!type) return <span className="text-slate-400 tabular-nums">—</span>;
  return (
    <EnterpriseBadge
      label={MAP_TYPE_LABELS[type]}
      color={MAP_TYPE_COLORS[type]}
    />
  );
}

function MetricCell({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  const isEmpty = value === '—';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-widest text-slate-400 font-medium leading-none">{label}</span>
      <span className={`text-[11px] tabular-nums leading-none ${bold ? 'font-bold' : 'font-semibold'} ${isEmpty ? 'text-slate-300' : 'text-slate-700'}`}>
        {value}
      </span>
    </div>
  );
}

function ActionsBadge({ done, total }: { done: number; total: number }) {
  if (total === 0) return <span className="text-slate-300 tabular-nums text-[11px]">—</span>;
  const pct = Math.round((done / total) * 100);
  const color =
    pct === 100 ? 'text-emerald-600' :
    pct >= 50   ? 'text-blue-600'    :
                  'text-amber-600';
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-[11px] tabular-nums font-semibold leading-none ${color}`}>
        {done}/{total}
      </span>
      <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3, 4, 5].map(i => (
        <tr key={i} className="enterprise-table-row animate-pulse">
          {[80, 200, 90, 80, 160, 80].map((w, j) => (
            <td key={j} className="px-3 py-2">
              <div className="h-3 rounded-[2px] enterprise-surface-3" style={{ width: w }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface FluxosListFiltersProps {
  status: ProjectStatus | '';
  mapType: MapType | '';
  search: string;
  onStatusChange: (value: ProjectStatus | '') => void;
  onMapTypeChange: (value: MapType | '') => void;
  onSearchChange: (value: string) => void;
}

interface CentralFluxosPageProps {
  /** Quando fornecido (modo aba), abrir um fluxo delega ao pai em vez de navegar via URL. */
  onOpenFluxo?: (projectId: string, projectName?: string | null, mapType?: MapType | null) => void;
  /** Modo embutido: renderiza sem ModuleLayout/ribbon próprio; filtros ficam no ribbon do Mapa. */
  embedded?: boolean;
  /** Filtros controlados pelo pai (modo aba do MapaPage). */
  filters?: FluxosListFiltersProps;
  /** Registra callback de atualização para o ribbon do pai. */
  onRegisterRefresh?: (refresh: () => void) => void;
}

export const CentralFluxosPage: React.FC<CentralFluxosPageProps> = ({
  onOpenFluxo,
  embedded = false,
  filters,
  onRegisterRefresh,
}) => {
  const navigate   = useNavigate();
  const orgId      = useOrganizationId();
  const { projects: ctxProjects, selectedProject, setSelectedProject, setProjects, updateProject } = useProjectContext();
  const currentProjectId = useProjectStore(state => state.currentProjectId);
  const setCurrentMapDisplayName = useProjectStore(state => state.setCurrentMapDisplayName);
  const beginNewMapDraft = useProjectStore(state => state.beginNewMapDraft);
  const resetNewProjectState = useProjectStore(state => state.resetNewProjectState);
  const { showToast, ToastContainer } = useAppToast();

  // ── Data state
  const [rows, setRows]           = useState<FluxoRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rowMenu, setRowMenu] = useState<RowMenuState | null>(null);
  const [menuBusy, setMenuBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // ── Filters (internos quando não controlados pelo pai)
  const [localFilterStatus, setLocalFilterStatus] = useState<ProjectStatus | ''>('');
  const [localFilterMapType, setLocalFilterMapType] = useState<MapType | ''>('');
  const [localFilterSearch, setLocalFilterSearch] = useState('');

  const filterStatus = filters?.status ?? localFilterStatus;
  const filterMapType = filters?.mapType ?? localFilterMapType;
  const filterSearch = filters?.search ?? localFilterSearch;
  const setFilterStatus = filters?.onStatusChange ?? setLocalFilterStatus;
  const setFilterMapType = filters?.onMapTypeChange ?? setLocalFilterMapType;
  const setFilterSearch = filters?.onSearchChange ?? setLocalFilterSearch;

  // ── Abort controller for cleanup
  const abortRef = useRef<AbortController | null>(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const loadFluxos = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      setRows([]);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setFetchError(null);

    try {
      // 1. Projects com dados mínimos para listagem (incluindo data JSONB para métricas)
      const { data: projectsRaw, error: projErr } = await supabase
        .from('projects')
        .select('*')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (projErr) {
        const msg = projErr.message ?? projErr.details ?? projErr.hint ?? JSON.stringify(projErr);
        console.warn('[CentralFluxosPage] projErr:', projErr);
        throw new Error(msg);
      }
      const projects = ((projectsRaw ?? []) as RawProjectRow[])
        .filter(project => !isRetiredProject(project));

      // Build action counts only when there are Supabase projects
      const projectIds = projects.map(p => p.id);

      // 2. Contagem de ações vinculadas (dois counts em paralelo)
      const [{ data: allActions }, { data: doneActions }] = projectIds.length > 0
        ? await Promise.all([
            supabase
              .from('actions')
              .select('project_id')
              .eq('organization_id', orgId)
              .in('project_id', projectIds),
            supabase
              .from('actions')
              .select('project_id')
              .eq('organization_id', orgId)
              .in('project_id', projectIds)
              .in('status', ['completed', 'done', 'closed', 'cancelled']),
          ])
        : [{ data: [] }, { data: [] }];

      // Montar mapa de contagens por project_id
      const actionCounts = new Map<string, { total: number; done: number }>();
      for (const id of projectIds) {
        actionCounts.set(id, { total: 0, done: 0 });
      }
      for (const a of (allActions ?? []) as Array<{ project_id: string | null }>) {
        if (!a.project_id) continue;
        const c = actionCounts.get(a.project_id);
        if (c) c.total += 1;
      }
      for (const a of (doneActions ?? []) as Array<{ project_id: string | null }>) {
        if (!a.project_id) continue;
        const c = actionCounts.get(a.project_id);
        if (c) c.done += 1;
      }

      // 3. Merge with localStorage canvas projects (not yet synced to Supabase)
      const supabaseIds = new Set(projects.map(p => p.id));
      let localOnlyRows: FluxoRow[] = [];
      try {
        const localRaw = localStorage.getItem('lean_workflow_projects');
        const localProjects: CanvasProject[] = localRaw ? JSON.parse(localRaw) : [];
        const notInSupabase = localProjects.filter(p => p.id && !supabaseIds.has(p.id));
        localOnlyRows = notInSupabase.map(p => toFluxoRow(canvasProjectToRaw(p), new Map(), true));
      } catch (localErr) {
        console.warn('[CentralFluxos] localStorage merge error:', localErr);
      }

      setRows([
        ...projects.map(p => toFluxoRow(p, actionCounts, false)),
        ...localOnlyRows,
      ]);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error
        ? err.message
        : (typeof err === 'object' && err !== null && 'message' in err)
          ? String((err as Record<string, unknown>).message)
          : JSON.stringify(err);
      console.warn('[CentralFluxosPage] Fetch failed:', err);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadFluxos();
    return () => abortRef.current?.abort();
  }, [loadFluxos]);

  useEffect(() => {
    onRegisterRefresh?.(loadFluxos);
    return () => onRegisterRefresh?.(() => {});
  }, [loadFluxos, onRegisterRefresh]);

  // Re-fetch when the tab becomes visible again (e.g., returning from MapaPage after a save)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Always refetch when returning to this tab — catches both dirty Supabase saves
      // and canvas-only localStorage changes (which don't set the flag before this fix)
      localStorage.removeItem('wf_central_fluxos_dirty');
      loadFluxos();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadFluxos]);

  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !menuBusy) close();
    };
    const onPointer = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      if (!menuBusy) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', close, true);
    };
  }, [rowMenu, menuBusy]);

  const syncOpenMapName = useCallback((id: string, name: string) => {
    if (currentProjectId === id) setCurrentMapDisplayName(name);
    if (selectedProject?.id === id) {
      setSelectedProject({ ...selectedProject, nome_projeto: name });
      updateProject(id, { nome_projeto: name });
    }
  }, [currentProjectId, selectedProject, setCurrentMapDisplayName, setSelectedProject, updateProject]);

  const renameFluxo = useCallback(async (row: FluxoRow, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === row.name) {
      setRowMenu(null);
      return;
    }
    setMenuBusy(true);
    try {
      if (!row.isLocalOnly) {
        if (!orgId) throw new Error('Nenhuma organização selecionada.');
        const { data: existing, error: readError } = await supabase
          .from('projects')
          .select('data')
          .eq('id', row.id)
          .eq('organization_id', orgId)
          .maybeSingle();
        if (readError) throw new Error(readError.message);
        const currentData = existing?.data && typeof existing.data === 'object'
          ? existing.data as Record<string, unknown>
          : {};
        const { error } = await supabase
          .from('projects')
          .update({
            name: trimmed,
            nome_projeto: trimmed,
            data: { ...currentData, nome_projeto: trimmed, name: trimmed },
          })
          .eq('id', row.id)
          .eq('organization_id', orgId);
        if (error) throw new Error(error.message);
      }
      updateLocalProject(row.id, trimmed);
      syncOpenMapName(row.id, trimmed);
      showToast('success', 'Mapa renomeado.');
      setRowMenu(null);
      await loadFluxos();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Não foi possível renomear o mapa.');
    } finally {
      setMenuBusy(false);
    }
  }, [loadFluxos, orgId, showToast, syncOpenMapName]);

  const duplicateFluxo = useCallback(async (row: FluxoRow) => {
    setMenuBusy(true);
    try {
      const nextName = copyLabel(row.name);
      const nextId = crypto.randomUUID();
      if (row.isLocalOnly) {
        if (!duplicateLocalProject(row.id, nextId, nextName)) {
          throw new Error('Mapa local não encontrado neste navegador.');
        }
      } else {
        if (!orgId) throw new Error('Nenhuma organização selecionada.');
        const { data: source, error: readError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', row.id)
          .eq('organization_id', orgId)
          .maybeSingle();
        if (readError) throw new Error(readError.message);
        if (!source) throw new Error('Mapa não encontrado.');
        const { data: authData, error: authError } = await supabase.auth.getUser();
        if (authError || !authData.user) throw new Error('Sessão expirada. Entre novamente.');
        const sourceData = source.data && typeof source.data === 'object'
          ? source.data as Record<string, unknown>
          : {};
        const { error } = await supabase.from('projects').insert({
          id: nextId,
          name: nextName,
          nome_projeto: nextName,
          description: source.description ?? null,
          status: source.status ?? 'draft',
          data: { ...sourceData, id: nextId, nome_projeto: nextName, name: nextName },
          flow_type: source.flow_type ?? null,
          project_type: source.project_type ?? 'vsm',
          visibility_scope: source.visibility_scope ?? 'organization',
          visibility: source.visibility ?? 'published',
          organization_id: orgId,
          owner_id: authData.user.id,
          created_by: authData.user.id,
          user_id: authData.user.id,
          progress: source.progress ?? 0,
          created_at: new Date().toISOString(),
          plt_value: source.plt_value ?? null,
          vat_value: source.vat_value ?? null,
        });
        if (error) throw new Error(error.message);
      }
      showToast('success', 'Mapa duplicado.');
      setRowMenu(null);
      await loadFluxos();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Não foi possível duplicar o mapa.');
    } finally {
      setMenuBusy(false);
    }
  }, [loadFluxos, orgId, showToast]);

  const deleteFluxo = useCallback(async (row: FluxoRow) => {
    setMenuBusy(true);
    try {
      if (!row.isLocalOnly) {
        if (!orgId) throw new Error('Nenhuma organização selecionada.');
        await retireCloudProject(row.id, orgId);
      }
      removeLocalProject(row.id);
      setProjects(ctxProjects.filter(project => project.id !== row.id));
      setRows(current => current.filter(item => item.id !== row.id));
      if (currentProjectId === row.id) {
        beginNewMapDraft();
        resetNewProjectState();
        setSelectedProject(null);
      }
      if (selectedId === row.id) setSelectedId(null);
      showToast('success', 'Mapa excluído.');
      setRowMenu(null);
      await loadFluxos();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível excluir o mapa.';
      const denied = /policy|permission|row-level|rls/i.test(message);
      showToast(
        'error',
        denied
          ? 'A nuvem recusou a exclusão. Libere a permissão de excluir mapas no banco.'
          : message,
      );
    } finally {
      setMenuBusy(false);
    }
  }, [beginNewMapDraft, ctxProjects, currentProjectId, loadFluxos, orgId, resetNewProjectState, selectedId, setProjects, setSelectedProject, showToast]);

  const openRowMenu = useCallback((row: FluxoRow, event: React.MouseEvent<HTMLTableRowElement>) => {
    const point = placeMenu(event.clientX, event.clientY, 132);
    setSelectedId(row.id);
    setRowMenu({ mode: 'actions', row, ...point });
  }, []);

  // ─── Filtered rows ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = rows;
    if (filterStatus)  result = result.filter(r => r.status === filterStatus);
    if (filterMapType) result = result.filter(r => r.mapType === filterMapType);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(
        r => r.name.toLowerCase().includes(q) ||
             (r.description ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [rows, filterStatus, filterMapType, filterSearch]);

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const openFluxo = useCallback((row: FluxoRow) => {
    // Modo aba: delega ao pai (troca para o canvas); caso contrário, navega via URL
    if (onOpenFluxo) {
      onOpenFluxo(row.id, row.name, row.mapType);
      return;
    }
    navigate(`/mapa?projectId=${row.id}`);
  }, [navigate, onOpenFluxo]);

  // ─── KPI strip ──────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const total     = rows.length;
    const active    = rows.filter(r => r.status === 'active').length;
    const withMetrics = rows.filter(r => r.metrics.efficiency !== null);
    const avgEff    = withMetrics.length > 0
      ? Math.round(withMetrics.reduce((s, r) => s + (r.metrics.efficiency ?? 0), 0) / withMetrics.length)
      : null;
    const industrial = rows.filter(r => r.mapType === 'FACTORY').length;
    const office     = rows.filter(r => r.mapType === 'OFFICE').length;
    return { total, active, avgEff, industrial, office };
  }, [rows]);

  // ─── Grid columns ───────────────────────────────────────────────────────────

  const columns = useMemo((): DataGridColumn<FluxoRow>[] => [
    {
      key: 'status',
      label: 'Status',
      width: '110px',
      render: row => <StatusBadge status={row.status} />,
    },
    {
      key: 'name',
      label: 'Fluxo / Mapa',
      sortable: true,
      render: row => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[12px] font-medium text-slate-800 leading-tight truncate">{row.name}</span>
            {row.isLocalOnly && (
              <span className="shrink-0 px-1 py-px text-[8px] font-semibold uppercase tracking-wide rounded-sm bg-amber-100 text-amber-700 border border-amber-200">
                local
              </span>
            )}
          </div>
          {row.description && (
            <span className="text-[10px] text-slate-400 truncate leading-tight">{row.description}</span>
          )}
        </div>
      ),
    },
    {
      key: 'mapType',
      label: 'Tipo',
      width: '130px',
      render: row => <MapTypeBadge type={row.mapType} />,
    },
    {
      key: 'metrics',
      label: 'Indicadores Críticos',
      width: '260px',
      render: row => {
        const isOffice = row.mapType === 'OFFICE';
        const label1 = isOffice ? 'TP'  : 'PLT';
        const label2 = isOffice ? 'TRA' : 'VAT';
        return (
          <div className="flex items-center gap-4">
            <MetricCell label={label1}     value={fmtMinutes(row.metrics.plt)} />
            <MetricCell label={label2}     value={fmtMinutes(row.metrics.vat)} />
            <MetricCell label="Eficiência" value={fmtPct(row.metrics.efficiency)} bold />
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '90px',
      align: 'center',
      render: row => (
        <div className="flex justify-center">
          <ActionsBadge done={row.actionsDone} total={row.actionsTotal} />
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Criado em',
      width: '110px',
      mono: true,
      sortable: true,
      render: row => (
        <span className="text-[11px] text-slate-500 tabular-nums">{fmtDate(row.createdAt)}</span>
      ),
    },
  ], []);

  // ─── Ribbon ─────────────────────────────────────────────────────────────────

  const ribbon = (
    <EnterpriseRibbon moduleId="fluxos">
      <EnterpriseRibbon.Group>
        <RibbonDesktopButton
          icon={<RefreshCw strokeWidth={1.5} />}
          label="Atualizar"
          onClick={loadFluxos}
        />
        {selectedId && (
          <RibbonDesktopButton
            icon={<ExternalLink strokeWidth={1.5} />}
            label="Abrir Mapa"
            onClick={() => {
              const row = rows.find(r => r.id === selectedId);
              if (row) openFluxo(row);
            }}
          />
        )}
      </EnterpriseRibbon.Group>

      <EnterpriseRibbon.Filters>
        <FluxosListRibbonFilters
          filterMapType={filterMapType}
          filterStatus={filterStatus}
          onMapTypeChange={setFilterMapType}
          onStatusChange={setFilterStatus}
        />
        <div className="relative flex items-center">
          <Search size={11} className="pointer-events-none absolute left-2 text-slate-400 z-10" />
          <input
            type="text"
            placeholder="Buscar fluxo..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            className={`pl-6 pr-2 w-40 ${desktopRibbonInputClass}`}
          />
        </div>
      </EnterpriseRibbon.Filters>
    </EnterpriseRibbon>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  const content = (
    <>
      {/* KPI Strip */}
      {!loading && rows.length > 0 && (
        <div className="flex items-center gap-6 px-6 py-2.5 border-b border-slate-200 bg-white shrink-0">
          <KpiStat icon={<MapIcon size={12} />}      label="Total"        value={String(kpis.total)} />
          <KpiStat icon={<TrendingUp size={12} />}   label="Ativos"       value={String(kpis.active)} highlight />
          <KpiStat icon={<Factory size={12} />}      label="Industrial"   value={String(kpis.industrial)} />
          <KpiStat icon={<Layers size={12} />}       label="Lean Office"  value={String(kpis.office)} />
          {kpis.avgEff !== null && (
            <KpiStat icon={<Clock size={12} />}      label="Efic. Média"  value={`${kpis.avgEff}%`} />
          )}
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {fetchError ? (
          <ErrorState message={fetchError} onRetry={loadFluxos} />
        ) : (
          <DataGridEnterprise<FluxoRow>
            columns={columns}
            data={filtered}
            rowKey={r => r.id}
            isLoading={loading}
            selectedKey={selectedId}
            onRowClick={r => setSelectedId(prev => prev === r.id ? null : r.id)}
            onRowDoubleClick={openFluxo}
            onRowContextMenu={openRowMenu}
            rowActions={row => (
              <button
                onClick={e => { e.stopPropagation(); openFluxo(row); }}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-sm bg-white shadow-sm transition-colors"
                title="Abrir canvas"
              >
                <ExternalLink size={10} />
                Abrir
              </button>
            )}
            emptyIcon={<MapIcon size={28} className="text-slate-300" />}
            emptyTitle="Nenhum fluxo cadastrado"
            emptySubtitle={
              filterStatus || filterMapType || filterSearch
                ? 'Nenhum resultado para os filtros aplicados.'
                : 'Crie um projeto com o módulo VSM ativo para visualizá-lo aqui.'
            }
          />
        )}
      </div>
    </>
  );

  const menu = rowMenu ? (
    <FluxoRowMenu
      menuRef={menuRef}
      state={rowMenu}
      busy={menuBusy}
      onRename={() => setRowMenu({
        mode: 'rename',
        row: rowMenu.row,
        x: rowMenu.x,
        y: rowMenu.y,
        draft: rowMenu.row.name,
      })}
      onDraftChange={draft => setRowMenu(current => (
        current?.mode === 'rename' ? { ...current, draft } : current
      ))}
      onConfirmRename={() => {
        if (rowMenu.mode === 'rename') void renameFluxo(rowMenu.row, rowMenu.draft);
      }}
      onDuplicate={() => { void duplicateFluxo(rowMenu.row); }}
      onAskDelete={() => setRowMenu({
        mode: 'confirm-delete',
        row: rowMenu.row,
        x: rowMenu.x,
        y: rowMenu.y,
      })}
      onConfirmDelete={() => { void deleteFluxo(rowMenu.row); }}
      onCancel={() => { if (!menuBusy) setRowMenu(null); }}
    />
  ) : null;

  // Modo embutido: filtros e busca ficam no ribbon do MapaPage (sem barra extra).
  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--bg-app)]">
        {content}
        {menu}
        <ToastContainer />
      </div>
    );
  }

  return (
    <ModuleLayout ribbon={ribbon}>
      {content}
      {menu}
      <ToastContainer />
    </ModuleLayout>
  );
};

// ─── Micro-components ─────────────────────────────────────────────────────────

function FluxoRowMenu({
  menuRef,
  state,
  busy,
  onRename,
  onDraftChange,
  onConfirmRename,
  onDuplicate,
  onAskDelete,
  onConfirmDelete,
  onCancel,
}: {
  menuRef: React.RefObject<HTMLDivElement | null>;
  state: RowMenuState;
  busy: boolean;
  onRename: () => void;
  onDraftChange: (value: string) => void;
  onConfirmRename: () => void;
  onDuplicate: () => void;
  onAskDelete: () => void;
  onConfirmDelete: () => void;
  onCancel: () => void;
}) {
  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[80] w-[204px] rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-lg"
      style={{ left: state.x, top: state.y }}
      onMouseDown={event => event.stopPropagation()}
      onContextMenu={event => event.preventDefault()}
    >
      {state.mode === 'actions' && (
        <>
          <MenuButton icon={<Pencil size={13} />} label="Renomear" disabled={busy} onClick={onRename} />
          <MenuButton icon={<Copy size={13} />} label="Duplicar" disabled={busy} onClick={onDuplicate} />
          <MenuButton icon={<Trash2 size={13} />} label="Excluir" disabled={busy} danger onClick={onAskDelete} />
        </>
      )}
      {state.mode === 'rename' && (
        <form
          className="flex flex-col gap-2 px-2 py-1.5"
          onSubmit={event => {
            event.preventDefault();
            onConfirmRename();
          }}
        >
          <label className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
            Renomear mapa
          </label>
          <input
            autoFocus
            value={state.draft}
            disabled={busy}
            onChange={event => onDraftChange(event.target.value)}
            className="w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-app)] px-2 py-1 text-[12px] text-[var(--text-primary)] outline-none"
          />
          <div className="flex justify-end gap-1">
            <button type="button" disabled={busy} onClick={onCancel} className="px-2 py-1 text-[11px] text-[var(--text-muted)]">
              Cancelar
            </button>
            <button type="submit" disabled={busy || !state.draft.trim()} className="rounded-sm bg-blue-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50">
              Salvar
            </button>
          </div>
        </form>
      )}
      {state.mode === 'confirm-delete' && (
        <div className="flex flex-col gap-2 px-2 py-1.5">
          <p className="text-[12px] leading-snug text-[var(--text-primary)]">
            Excluir <span className="font-semibold">{state.row.name}</span>?
          </p>
          <div className="flex justify-end gap-1">
            <button type="button" disabled={busy} onClick={onCancel} className="px-2 py-1 text-[11px] text-[var(--text-muted)]">
              Cancelar
            </button>
            <button type="button" disabled={busy} onClick={onConfirmDelete} className="rounded-sm bg-red-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50">
              Excluir
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

function MenuButton({
  icon,
  label,
  onClick,
  disabled,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] disabled:opacity-50 ${
        danger
          ? 'text-red-600 hover:bg-red-500/10'
          : 'text-[var(--text-primary)] hover:bg-[var(--button-hover)]'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function KpiStat({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-slate-400">{icon}</span>
      <div className="flex flex-col">
        <span className="text-[9px] uppercase tracking-widest text-slate-400 font-medium leading-none">{label}</span>
        <span className={`text-[13px] font-bold tabular-nums leading-tight ${highlight ? 'text-blue-600' : 'text-slate-700'}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <CheckSquare size={28} className="text-red-300" />
      <p className="text-[13px] font-medium text-red-600">Falha ao carregar fluxos</p>
      <p className="text-[11px] text-slate-400 max-w-sm">{message}</p>
      <button
        onClick={onRetry}
        className="mt-1 text-[11px] text-blue-600 underline hover:no-underline"
      >
        Tentar novamente
      </button>
    </div>
  );
}

export default CentralFluxosPage;
